import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
    Shield, Users, Award, Target, ChevronRight, CheckCircle2,
    Building, Clock, MapPin, Phone, Mail, Star, Heart, Eye
} from 'lucide-react';

const milestones = [
    { year: '2007', title: 'Company Founded', description: 'Established by Mr. Inam Singh Yadav (Ex-Army) with a vision to provide professional security services.' },
    { year: '2012', title: 'Service Expansion', description: 'Expanded operations by introducing armed security personnel and professional housekeeping staff, catering to IT companies, malls, and hospitals.' },
    { year: '2016', title: '500+ Guards Milestone', description: 'Reached 500 active security personnel serving diverse clients across multiple sectors.' },
    { year: '2020', title: 'Premier Provider Status', description: 'Recognized as a premier security services provider with 13+ years of trusted excellence.' },
    { year: '2022', title: 'PSARA License Renewed', description: 'Successfully renewed PSARA license, reinforcing our commitment to legal compliance.' },
];

const values = [
    { 
        icon: Shield, 
        title: 'Integrity', 
        description: 'We maintain the highest ethical standards in all our operations and client interactions.' 
    },
    { 
        icon: Target, 
        title: 'Excellence', 
        description: 'We strive for excellence in every security solution we provide to our clients.' 
    },
    { 
        icon: Heart, 
        title: 'Dedication', 
        description: 'Our team is dedicated to ensuring the safety and security of all our clients.' 
    },
    { 
        icon: Eye, 
        title: 'Vigilance', 
        description: 'We maintain constant vigilance and alertness in all our security operations.' 
    },
];

const team = [
    {
        role: 'Security Officers',
        description: 'Primarily from the army, holding Junior Commissioned Officers (JCOs) rank, with extensive experience in security operations.',
        count: '50+',
    },
    {
        role: 'Supervisors',
        description: 'Skilled and well-trained professionals in the art of security management and team coordination.',
        count: '30+',
    },
    {
        role: 'Head Guards',
        description: 'Senior guards who act as supervisors and ensure seamless operations in all situations.',
        count: '100+',
    },
    {
        role: 'Armed Guards',
        description: 'Personnel with valid gun-licenses, including ex-army professionals and trained civilians.',
        count: '80+',
    },
];

const certifications = [
    { label: 'PSARA License', value: 'Governed by PSARA 2005' },
    { label: 'Registered Under', value: 'Government of Karnataka' },
    { label: 'GSTIN', value: '29CSWP8056Q2Z8' },
];

const leadership = [
    {
        name: 'Mr. Inam Singh Yadav',
        role: 'Founder',
        image: 'https://customer-assets.emergentagent.com/job_securityweb-2/artifacts/ipuvdm8u_image.png',
        description: 'Ex-Army veteran who founded Shiva Security Services in 2007 with a vision to provide professional security solutions. His military background instilled values of discipline, integrity, and unwavering commitment that form the foundation of our organization.',
        highlight: 'Ex-Army | Visionary Leader'
    },
    {
        name: 'Mr Manoj Kumar',
        role: 'Managing Director',
        image: 'https://customer-assets.emergentagent.com/job_securityweb-2/artifacts/nb1s3nwx_image.png',
        description: 'Under his leadership, the organization has achieved operational excellence and sustained growth. He oversees professional management practices and ensures the company maintains its position as a premier security services provider.',
        highlight: 'Operational Excellence | Professional Management'
    }
];

const serviceAreas = [
    'IT Companies',
    'Malls & Commercial Complexes',
    'Hospitals',
    'Government Organizations',
    'Banks & ATMs',
    'Construction Companies',
    'Residential & Service Apartments',
    'Cinema Halls',
    'Amusement Parks',
    'Hotels',
    'Event Management Units'
];

export const AboutPage = () => {
    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[50vh] lg:min-h-[60vh] flex items-center pt-24 pb-12">
                {/* Background */}
                <div className="absolute inset-0 bg-muted/50" />
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
                {/* Watermark Image - Hero Only */}
                <div 
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ 
                        backgroundImage: 'url(https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1920&h=1080&fit=crop)',
                        filter: 'saturate(0.3) brightness(1.1)',
                        opacity: '0.06'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
                
                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="max-w-3xl">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-6 animate-fade-in">
                            <Building className="w-3 h-3 mr-1" />
                            ABOUT US
                        </Badge>
                        <h1 className="font-display text-5xl lg:text-7xl text-foreground leading-tight mb-6 animate-fade-in animation-delay-100">
                            YOUR TRUSTED SECURITY
                            <span className="text-accent block">PARTNER SINCE 2007</span>
                        </h1>
                        <p className="text-muted-foreground text-lg lg:text-xl leading-relaxed animate-fade-in animation-delay-200">
                            Founded by Ex-Army veteran Mr. Inam Singh Yadav, Shiva Security Services 
                            has grown into a premier security provider, delivering professional protection 
                            solutions across Bangalore.
                        </p>
                    </div>
                </div>
                
                {/* Horizontal Divider */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            </section>

            {/* Leadership Section - Now First */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            <Users className="w-3 h-3 mr-1" />
                            LEADERSHIP
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            VISIONARY LEADERSHIP
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Guided by experience, driven by excellence — our leadership team brings 
                            decades of expertise in security and management.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {/* Founder Card - Image can be cropped */}
                        <Card className="card-elevated overflow-hidden group hover:border-accent/30">
                            <div className="flex flex-col">
                                <div className="h-72 lg:h-80 overflow-hidden">
                                    <img 
                                        src={leadership[0].image}
                                        alt={leadership[0].name}
                                        className="w-full h-full object-cover object-top"
                                    />
                                </div>
                                <div className="p-6 lg:p-8">
                                    <Badge className="bg-accent/10 text-accent border-accent/20 mb-3 text-xs">
                                        {leadership[0].highlight}
                                    </Badge>
                                    <h3 className="font-display text-2xl text-foreground mb-1">{leadership[0].name}</h3>
                                    <p className="text-accent font-semibold text-sm mb-4">{leadership[0].role}</p>
                                    <p className="text-muted-foreground text-sm leading-relaxed">{leadership[0].description}</p>
                                </div>
                            </div>
                        </Card>

                        {/* Managing Director Card - Full photo visible */}
                        <Card className="card-elevated overflow-hidden group hover:border-accent/30">
                            <div className="flex flex-col">
                                <div className="h-72 lg:h-80 overflow-hidden bg-muted/30 flex items-center justify-center">
                                    <img 
                                        src={leadership[1].image}
                                        alt={leadership[1].name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="p-6 lg:p-8">
                                    <Badge className="bg-accent/10 text-accent border-accent/20 mb-3 text-xs">
                                        {leadership[1].highlight}
                                    </Badge>
                                    <h3 className="font-display text-2xl text-foreground mb-1">{leadership[1].name}</h3>
                                    <p className="text-accent font-semibold text-sm mb-4">{leadership[1].role}</p>
                                    <p className="text-muted-foreground text-sm leading-relaxed">{leadership[1].description}</p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Our Story Section - Now Second */}
            <section className="py-20 lg:py-32 bg-muted/30 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div className="order-2 lg:order-1">
                            <div className="relative">
                                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                                    <img 
                                        src="/images/about.jpg"
                                        alt="Security Team"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <Card className="absolute -bottom-8 -right-8 lg:-right-12 p-6 bg-card shadow-xl border-accent/20">
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-xl bg-accent flex items-center justify-center">
                                            <Clock className="w-8 h-8 text-accent-foreground" />
                                        </div>
                                        <div>
                                            <p className="font-display text-4xl text-foreground">17+</p>
                                            <p className="text-muted-foreground text-sm">Years Experience</p>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>
                        
                        <div className="order-1 lg:order-2">
                            <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                                OUR STORY
                            </Badge>
                            <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                                A LEGACY OF <span className="text-gradient-gold">PROTECTION</span>
                            </h2>
                            <div className="space-y-4 text-muted-foreground leading-relaxed">
                                <p>
                                    Founded in 2007 by Mr. Inam Singh Yadav, an Ex-Army veteran, 
                                    Shiva Security Services was established with a clear objective: 
                                    to provide professional, reliable security services that meet the 
                                    highest standards of excellence.
                                </p>
                                <p>
                                    Over more than 17 years, we have grown into a premier security 
                                    services provider, earning the trust of clients across diverse sectors 
                                    including IT companies, malls, hospitals, government organizations, 
                                    banks, construction companies, residential complexes, hotels, and 
                                    event management units.
                                </p>
                                <p>
                                    Our team, primarily comprising ex-army personnel and JCOs, brings 
                                    military-grade discipline and professionalism to every assignment. 
                                    We remain committed to innovative methods, modern technologies, and 
                                    evolving guarding techniques to deliver superior service quality.
                                </p>
                            </div>
                            <Link to="/contact" className="inline-block mt-8">
                                <Button className="btn-accent rounded-lg px-8 py-6 font-semibold">
                                    Get in Touch
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-20 lg:py-32 bg-muted/50 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="grid lg:grid-cols-2 gap-8">
                        <Card className="card-elevated p-8 lg:p-12 border-accent/20 group hover:border-accent/40">
                            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                                <Target className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="font-display text-3xl text-foreground mb-4">OUR MISSION</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                To provide comprehensive, reliable, and professional security services 
                                that exceed client expectations. We aim to be the first choice for 
                                businesses and individuals seeking trusted protection through trained 
                                personnel, cutting-edge practices, and unwavering commitment to safety.
                            </p>
                        </Card>
                        
                        <Card className="card-elevated p-8 lg:p-12 border-accent/20 group hover:border-accent/40">
                            <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-colors">
                                <Eye className="w-8 h-8 text-accent" />
                            </div>
                            <h3 className="font-display text-3xl text-foreground mb-4">OUR VISION</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                To be recognized as India&#39;s premier security service provider, 
                                setting industry standards for professionalism, reliability, and 
                                innovation. We envision a safer India where every client feels 
                                secure and protected through our comprehensive security solutions.
                            </p>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            CORE VALUES
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            WHAT DRIVES US
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Our values form the foundation of everything we do, guiding our 
                            actions and decisions in serving our clients.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <Card 
                                key={index}
                                className="card-elevated p-6 text-center group hover:border-accent/30"
                            >
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 group-hover:scale-110 transition-all duration-300">
                                    <value.icon className="w-8 h-8 text-accent" />
                                </div>
                                <h3 className="font-display text-xl text-foreground mb-2">{value.title}</h3>
                                <p className="text-muted-foreground text-sm">{value.description}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline Section */}
            <section className="py-20 lg:py-32 bg-hero-pattern px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/20 text-accent border-accent/30 mb-4">
                            OUR JOURNEY
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-primary-foreground mb-6">
                            MILESTONES
                        </h2>
                        <p className="text-primary-foreground/80 text-lg">
                            Key moments that have shaped our growth and success over the years.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="relative">
                            {/* Timeline Line */}
                            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-0.5 bg-accent/30 -translate-x-1/2" />
                            
                            {milestones.map((milestone, index) => (
                                <div 
                                    key={index}
                                    className={`relative flex items-start gap-8 mb-12 ${
                                        index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                                    }`}
                                >
                                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'} hidden lg:block`}>
                                        <Card className="inline-block p-6 bg-card/10 backdrop-blur border-accent/20">
                                            <p className="font-display text-3xl text-accent mb-2">{milestone.year}</p>
                                            <h3 className="font-display text-xl text-primary-foreground mb-2">{milestone.title}</h3>
                                            <p className="text-primary-foreground/70 text-sm">{milestone.description}</p>
                                        </Card>
                                    </div>
                                    
                                    <div className="absolute left-4 lg:left-1/2 w-8 h-8 rounded-full bg-accent flex items-center justify-center -translate-x-1/2 z-10">
                                        <div className="w-3 h-3 rounded-full bg-accent-foreground" />
                                    </div>
                                    
                                    <div className="flex-1 pl-12 lg:pl-0 lg:hidden">
                                        <Card className="p-6 bg-card/10 backdrop-blur border-accent/20">
                                            <p className="font-display text-3xl text-accent mb-2">{milestone.year}</p>
                                            <h3 className="font-display text-xl text-primary-foreground mb-2">{milestone.title}</h3>
                                            <p className="text-primary-foreground/70 text-sm">{milestone.description}</p>
                                        </Card>
                                    </div>
                                    
                                    <div className="flex-1 hidden lg:block" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Team Structure */}
            <section className="py-20 lg:py-32 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            OUR TEAM
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            PROFESSIONAL SECURITY PERSONNEL
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            Our team comprises highly trained professionals with diverse backgrounds, 
                            primarily from military and security services.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {team.map((member, index) => (
                            <Card 
                                key={index}
                                className="card-elevated p-8 flex gap-6 group hover:border-accent/30"
                            >
                                <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                                    <p className="font-display text-2xl text-accent">{member.count}</p>
                                </div>
                                <div>
                                    <h3 className="font-display text-xl text-foreground mb-2">{member.role}</h3>
                                    <p className="text-muted-foreground text-sm leading-relaxed">{member.description}</p>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Certifications */}
            <section className="py-20 lg:py-32 bg-muted/50 px-4 lg:px-8">
                <div className="container mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                            <Award className="w-3 h-3 mr-1" />
                            CERTIFICATIONS
                        </Badge>
                        <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-6">
                            LICENSED & COMPLIANT
                        </h2>
                        <p className="text-muted-foreground text-lg">
                            We maintain all necessary licenses and registrations required for 
                            professional security operations in India.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6 max-w-4xl mx-auto">
                        {certifications.map((cert, index) => (
                            <Card 
                                key={index}
                                className="card-elevated p-6 text-center"
                            >
                                <p className="text-muted-foreground text-xs uppercase tracking-wider mb-2">{cert.label}</p>
                                <p className="font-mono text-sm text-foreground break-all">{cert.value}</p>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 lg:px-8">
                <div className="container mx-auto">
                    {/* Gratitude Message */}
                    <Card className="card-elevated p-8 lg:p-12 mb-12 border-accent/20">
                        <div className="max-w-3xl mx-auto text-center">
                            <Badge className="bg-accent/10 text-accent border-accent/20 mb-4">
                                <Heart className="w-3 h-3 mr-1" />
                                MESSAGE FROM LEADERSHIP
                            </Badge>
                            <h3 className="font-display text-2xl lg:text-3xl text-foreground mb-4">
                                A Note of Gratitude
                            </h3>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                On behalf of the entire Shiva Security Services team, we extend our sincere 
                                gratitude to all our valued clients for their continued patronage and trust. 
                                Your support has been instrumental in our growth over the past 17 years.
                            </p>
                            <p className="text-muted-foreground leading-relaxed">
                                We remain committed to serving you with excellence and look forward to your 
                                continued support in the years ahead. Together, we will continue to set new 
                                standards in professional security services.
                            </p>
                            <p className="text-accent font-semibold mt-6">
                                — The Managing Director & Leadership Team
                            </p>
                        </div>
                    </Card>

                    <Card className="bg-hero-pattern p-8 lg:p-16 text-center">
                        <h2 className="font-display text-4xl lg:text-5xl text-primary-foreground mb-6">
                            READY TO WORK WITH US?
                        </h2>
                        <p className="text-primary-foreground/80 text-lg max-w-2xl mx-auto mb-8">
                            Join hundreds of satisfied clients who trust Shiva Security Services 
                            for their protection needs across India.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/contact">
                                <Button className="btn-accent rounded-lg px-8 py-6 text-lg font-semibold">
                                    Contact Us Today
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <a href="tel:+919606924394">
                                <Button 
                                    variant="outline" 
                                    className="rounded-lg px-8 py-6 text-lg font-semibold border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                                >
                                    <Phone className="w-5 h-5 mr-2" />
                                    +91-9606924394
                                </Button>
                            </a>
                        </div>
                    </Card>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;
